import { useTranslation } from 'react-i18next';

import CurrencyInput from '~/components/Forms/CurrencyInput';
import Typography from '~/components/Typography';
import type { FontSizes } from '~/interfaces/font_family';
import type { Price } from '~/interfaces/subscriptions';
import { usePricingContext } from '~/pages/upgrade/_content/usePricingContext';

import currencyFormatter from '../../../utils/currencyFormatter';

interface Props {
  fontFamily: string;
  priceFontSize: FontSizes;
  accentColor: string;
  price: Price;
  txtColor: string;
  isFreePlan: boolean;
  isExternalCheckoutEnabled: boolean;
  isDonationPlan?: boolean;
}

const TierPriceBlock = ({
  fontFamily,
  priceFontSize,
  accentColor,
  price,
  txtColor,
  isFreePlan,
  isExternalCheckoutEnabled,
  isDonationPlan = false,
}: Props) => {
  const isDiscounted = price.amount_cents !== price.original_amount_cents;
  const { donationAmountCents, setDonationAmountCents } = usePricingContext();
  const { t } = useTranslation('', { keyPrefix: 'upgrade' });
  const formatter = currencyFormatter({ currency: price.currency });

  const isPaidTrial = price.premium_offer?.offer_type === 'paid_trial';
  const paidTrialOffer = isPaidTrial ? price.premium_offer : undefined;
  const paidTrialInlineSuffix = paidTrialOffer?.paid_trial_inline_suffix ?? null;
  const paidTrialAfterText = paidTrialOffer?.paid_trial_after_text ?? null;

  return (
    <div className="block">
      <div className="space-y-1">
        {!isExternalCheckoutEnabled && (
          <Typography
            size="sm"
            weight="bold"
            fontFamily={fontFamily}
            color={accentColor}
            className="capitalize truncate"
          >
            {price.cta}
          </Typography>
        )}

        <div className="flex flex-wrap w-full">
          {isDiscounted && !isDonationPlan && !isPaidTrial && (
            <Typography
              as="h4"
              size={priceFontSize}
              weight="semibold"
              fontFamily={fontFamily}
              color={txtColor}
              className="mr-1 line-through opacity-25"
            >
              {formatter.format((price.original_amount_cents || 0) / 100)}
            </Typography>
          )}
          <Typography as="h4" size={priceFontSize} weight="semibold" fontFamily={fontFamily} color={txtColor}>
            <div className="flex flex-row items-end">
              {isDonationPlan ? (
                isExternalCheckoutEnabled ? (
                  <Typography
                    as="span"
                    size="xl"
                    weight="semibold"
                    fontFamily={fontFamily}
                    color={txtColor}
                    className="ml-1"
                  >
                    {t('donation.name_your_price')}
                  </Typography>
                ) : (
                  <CurrencyInput
                    name={`donation-amount-${price.id}`}
                    value={donationAmountCents}
                    onChange={(value) => setDonationAmountCents(value)}
                    minCents={100}
                    maxCents={100000}
                    currency={price.currency}
                    className="w-full"
                    required
                  />
                )
              ) : isPaidTrial && paidTrialOffer?.paid_trial_price_cents != null ? (
                formatter.format(paidTrialOffer.paid_trial_price_cents / 100)
              ) : (
                formatter.format((price.amount_cents || 0) / 100)
              )}
              {!isFreePlan && !isPaidTrial && price.denominator && (
                <Typography
                  as="span"
                  size="xl"
                  weight="semibold"
                  fontFamily={fontFamily}
                  color={txtColor}
                  className="ml-1"
                >
                  /{price.denominator}
                </Typography>
              )}
              {isPaidTrial && paidTrialInlineSuffix && (
                <Typography
                  as="span"
                  size="sm"
                  weight="regular"
                  fontFamily={fontFamily}
                  color={txtColor}
                  className="ml-1 mb-1"
                >
                  {paidTrialInlineSuffix}
                </Typography>
              )}
            </div>
          </Typography>
        </div>
        {isPaidTrial && paidTrialAfterText && (
          <Typography size="xs" weight="light" fontFamily={fontFamily} color={txtColor} className="opacity-60">
            {paidTrialAfterText}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default TierPriceBlock;
